import { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardActions,
    CardContent,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Switch,
    Typography
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import ReorderIcon from '@mui/icons-material/Reorder';
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TopicIcon from '@mui/icons-material/Topic';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { ResultType } from '../../utils/custom.ResultTypeMonths';

const RegulationInfo = ({ expanded, regulationKey, regulation }) => {
    const [lowestGeographyName, setLowestGeographyName] = useState();
    const [icon, setIcon] = useState();
    const [subjectIcon, setSubjectIcon] = useState();
    const [expand, setExpand] = useState(expanded);

    useEffect(() => {
        if (regulation.Port.length > 0) {
            setLowestGeographyName(regulation.Port);
            setIcon(<DirectionsBoatIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.State.length > 0) {
            setLowestGeographyName(regulation.State);
            setIcon(<AccountBalanceIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.Country.length > 0) {
            setLowestGeographyName(regulation.Country);
            setIcon(<FlagIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.Region.length > 0) {
            setLowestGeographyName(regulation.Region);
            setIcon(<SouthAmericaIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.Campaign.length > 0) {
            setLowestGeographyName(`All countries in the ${regulation.Campaign}`);
            setIcon(<FlagIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        }

        if (regulation.Sbject === 'Ports Incentives') {
            setSubjectIcon(<AttachMoneyIcon sx={{ height: '24px', margin: '0 0px 0 8px' }} />);
        } else {
            setSubjectIcon(<TopicIcon sx={{ height: '24px', margin: '0 2px 0 12px' }} />);
        }
    }, [regulation, regulationKey, expanded]);

    return (
        <Paper elevation={3} sx={{ marginBottom: '32px', width: { xs: '340px', sm: '100%' }, marginLeft: { xs: '12px', sm: '0' } }}>
            <Card>
                <CardContent>
                    <Stack>
                        <Stack direction="row">
                            {icon}
                            <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                {lowestGeographyName}
                            </Typography>
                            {subjectIcon}
                            <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                {regulation.Subject}
                            </Typography>
                            {regulation.Campaign.length > 0 && (
                                <>
                                    <LocalPoliceIcon color="red" sx={{ height: '24px', margin: '0 2px 0 4px' }} />
                                    <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                        {regulation.Campaign} inspection campaign
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: '11px', margin: '4px 0 0 6px' }}>{`${regulation.StartDate}-${regulation.EndDate}`}</Typography>
                                </>
                            )}
                        </Stack>
                        <Typography variant="h6" component="div" sx={{ padding: '10px 0', lineHeight: '1.2' }}>
                            {regulation.Name}{' '}
                        </Typography>
                        <CardActions sx={{ padding: '8px 0' }}>
                            <Link href={regulation.Link_Url} underline="always" target="_blank">
                                {regulation.Link_Display}
                            </Link>
                        </CardActions>
                    </Stack>
                    <Accordion
                        disableGutters={true}
                        sx={{ backgroundColor: '#FFFFFF', marginTop: '24px', boxShadow: 'none' }}
                        expanded={expanded || expand}
                        onChange={(event, exp) => {
                            setExpand(exp);
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                            {regulation.SpecialArea.length > 0 && (
                                <Stack direction="row" sx={{ marginTop: '8px' }}>
                                    <InfoIcon color="red" sx={{ height: '16px', marginTop: '2px' }} />
                                    <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                        Application guidance only applies to {regulation.SpecialArea}
                                    </Typography>
                                </Stack>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: regulation.Description }} />
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </Paper>
    );
};

const RegulationItemUpdatedOn = ({ regulationData, resultTypeSelected, onResultTypeChange }) => {
    // Pagination
    const [currentPageData, setCurrentPageData] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageRowOption, setPageRowOption] = useState(5);

    // Filters
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        setPageData(1, pageRowOption);
        setPageNumber(1);
    }, [regulationData]);

    const handleExpandAllChange = () => {
        setExpandAll(!expandAll);
    };

    const handlePageChange = (event, pageNumber) => {
        setPageData(pageNumber, pageRowOption);
        setPageNumber(pageNumber);
    };

    const setPageData = (pageNumber, rowsPerPage) => {
        const startIndex = (pageNumber - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const tempPageData = regulationData.slice(startIndex, endIndex);

        setCurrentPageData(tempPageData);
    };

    return (
        <>
            <div>
                <Box sx={{ margin: { xs: '24px 0', sm: '24px 0', md: '32px 128px' } }}>
                    <Stack direction={'row'} spacing={2} sx={{ marginBottom: '24px', justifyContent: 'flex-start' }}>
                        <FormControl sx={{ minWidth: 120, display: { xs: 'none', sm: 'none', md: 'flex' } }} size="small">
                            <InputLabel>Showing</InputLabel>
                            <Select value={!resultTypeSelected ? ResultType.threeMonths : resultTypeSelected} onChange={onResultTypeChange} label="Showing">
                                <MenuItem value={ResultType.threeMonths}>
                                    <ReorderIcon sx={{ height: '18px' }} />
                                    {ResultType.threeMonths}
                                </MenuItem>
                                <MenuItem value={ResultType.sixMonths}>
                                    <ReorderIcon sx={{ height: '18px' }} />
                                    {ResultType.sixMonths}
                                </MenuItem>
                            </Select>
                        </FormControl>
                        {/* Expand All */}
                        {currentPageData.length > 0 && (
                            <FormGroup key="1" sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, flexDirection: 'row' }}>
                                <FormControlLabel control={<Switch checked={expandAll} onChange={handleExpandAllChange} />} label="Expand All" />
                            </FormGroup>
                        )}
                    </Stack>

                    <Stack direction={'column'} spacing={2}>
                        {/* No Regulations To Show */}
                        {(!currentPageData || currentPageData.length <= 0) && (
                            <Typography variant="body1" component="div" sx={{ margin: '0' }}>
                                No specific guidance. Please change your filter criteria.
                            </Typography>
                        )}

                        {currentPageData && currentPageData.length > 0 && (
                            <>
                                {/* Top Pagination */}
                                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                    <div></div>
                                    <Pagination
                                        color="primary"
                                        count={Math.ceil(parseFloat(regulationData.length / pageRowOption))}
                                        page={pageNumber}
                                        onChange={handlePageChange}
                                        rowsPerPage={pageRowOption}
                                        rowsPerPageOptions={[5]}
                                        variant="outlined"
                                        shape="rounded"
                                    />
                                    <Typography variant="h6">No. Items : {`${regulationData.length}`}</Typography>
                                </Stack>

                                {/* Regulation Card Information */}
                                {currentPageData.map((regulation, index) => (
                                    <RegulationInfo expanded={expandAll} regulationKey={index} regulation={regulation} />
                                ))}

                                {/* Bottom Pagination */}
                                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                    <div></div>
                                    <Pagination
                                        color="primary"
                                        count={Math.ceil(parseFloat(regulationData.length / pageRowOption))}
                                        page={pageNumber}
                                        onChange={handlePageChange}
                                        rowsPerPage={pageRowOption}
                                        rowsPerPageOptions={[5]}
                                        variant="outlined"
                                        shape="rounded"
                                    />
                                    <Typography variant="h6">No. Items : {`${regulationData.length}`}</Typography>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Box>
            </div>
        </>
    );
};

export default RegulationItemUpdatedOn;
